import { useEffect, useState } from 'react';
import { getSlaMatrix, salvarSla, SlaMatrix } from '../api';
import { Seller, Regiao } from '../types';

interface Edicao {
  seller: Seller;
  regiao: Regiao;
  prazo: number | string;
  tipo: 'CORRIDAS' | 'UTEIS';
  novo: boolean;
}

export default function AdminRegioesSlas() {
  const [data, setData] = useState<SlaMatrix | null>(null);
  const [editando, setEditando] = useState<Edicao | null>(null);
  const [salvando, setSalvando] = useState(false);

  const load = () => getSlaMatrix().then(setData);
  useEffect(() => { load(); }, []);

  if (!data) return <p className="empty">Carregando matriz de SLAs…</p>;

  const slaDe = (sellerId: number, regiaoId: number) =>
    data.slas.find((s) => s.seller_id === sellerId && s.regiao_id === regiaoId);
  const faltantes = data.sellers.length * data.regioes.length - data.slas.length;

  const abrirEdicao = (seller: Seller, regiao: Regiao) => {
    const atual = slaDe(seller.seller_id, regiao.id);
    setEditando({
      seller, regiao,
      prazo: (atual?.prazo_horas ?? data.default_horas) / 24, // edição em DIAS
      tipo: atual?.tipo_contagem ?? 'CORRIDAS',
      novo: !atual,
    });
  };

  const salvar = async () => {
    if (!editando) return;
    setSalvando(true);
    await salvarSla(editando.seller.seller_id, editando.regiao.id, Math.round(Number(editando.prazo) * 24), editando.tipo); // dias → horas no modelo
    await load();
    setSalvando(false);
    setEditando(null);
  };

  return (
    <>
      <div className="page-header">
        <div>
          <h1>Regiões &amp; SLAs</h1>
          <div className="sub">SLA de entrega por seller × região, em dias · {faltantes} combinações sem SLA (usando default de {data.default_horas / 24} dias)</div>
        </div>
      </div>

      <div className="card">
        <h2>Regiões (conjuntos de UFs)</h2>
        <div className="table-wrap">
          <table>
            <thead>
              <tr><th>Região</th><th>UFs</th></tr>
            </thead>
            <tbody>
              {data.regioes.map((r) => (
                <tr key={r.id}>
                  <td><strong>{r.nome}</strong></td>
                  <td>{r.ufs.join(', ')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="card">
        <h2>Matriz de SLA — clique em uma célula para editar</h2>
        <div className="table-wrap">
          <table className="min-w-[720px]">
            <thead>
              <tr>
                <th>Seller</th>
                {data.regioes.map((r) => <th key={r.id}>{r.nome}</th>)}
              </tr>
            </thead>
            <tbody>
              {data.sellers.map((s) => (
                <tr key={s.seller_id}>
                  <td><strong>{s.nome_fantasia}</strong><div className="muted text-[11px]">{s.uf_base}</div></td>
                  {data.regioes.map((r) => {
                    const sla = slaDe(s.seller_id, r.id);
                    return (
                      <td key={r.id}>
                        <span
                          className={`sla-cell ${sla ? '' : 'missing'}`}
                          onClick={() => abrirEdicao(s, r)}
                          title={sla ? `Vigente desde ${sla.vigencia_inicio}` : `Sem SLA — default global de ${data.default_horas / 24} dias`}
                        >
                          {sla ? `${sla.prazo_horas / 24}d ${sla.tipo_contagem === 'UTEIS' ? 'úteis' : 'corr.'}` : `${data.default_horas / 24}d default`}
                        </span>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="chart-note">Células tracejadas usam o SLA default global — combinação sem configuração explícita.</p>
      </div>

      {editando && (
        <div className="modal-overlay" onClick={() => setEditando(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>{editando.novo ? 'Configurar' : 'Editar'} SLA — {editando.seller.nome_fantasia} × {editando.regiao.nome}</h2>
            <div className="form-group">
              <label>Prazo (dias)</label>
              <input
                type="number" min="1" value={editando.prazo}
                onChange={(e) => setEditando((ed) => (ed ? { ...ed, prazo: e.target.value } : ed))}
              />
            </div>
            <div className="form-group">
              <label>Tipo de contagem</label>
              <select value={editando.tipo} onChange={(e) => setEditando((ed) => (ed ? { ...ed, tipo: e.target.value as 'CORRIDAS' | 'UTEIS' } : ed))}>
                <option value="CORRIDAS">Dias corridos</option>
                <option value="UTEIS">Dias úteis</option>
              </select>
            </div>
            <p className="muted text-xs">
              Alterações criam nova vigência — pedidos passados continuam avaliados contra o SLA vigente na época.
            </p>
            <div className="modal-actions">
              <button className="btn secondary" onClick={() => setEditando(null)}>Cancelar</button>
              <button className="btn" onClick={salvar} disabled={salvando}>{salvando ? 'Salvando…' : 'Salvar'}</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
